import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { invalidateAvatar } from "@/components/Avatar";
import MonthDayPicker from "@/components/MonthDayPicker";
import { Save, Hash, MapPin, User as UserIcon, Camera, Trash2, Bell, BellOff, Send, Cake } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  iosNeedsInstall,
  currentSubscription,
  subscribePush,
  unsubscribePush,
  sendTestPush,
} from "../lib/push";

const POSITIONS = [
  "Gardien",
  "Défenseur central",
  "Latéral droit",
  "Latéral gauche",
  "Milieu défensif",
  "Milieu central",
  "Milieu offensif",
  "Ailier droit",
  "Ailier gauche",
  "Attaquant",
];

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: "", jersey_number: "", position: "", birthday: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        jersey_number: user.jersey_number ?? "",
        position: user.position || "",
        birthday: user.birthday || "",
      });
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim() || null,
        jersey_number:
          form.jersey_number === "" || form.jersey_number === null
            ? null
            : Number(form.jersey_number),
        position: form.position || null,
        birthday: form.birthday || "",
      };
      await api.put("/auth/me", payload);
      await refreshUser();
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-[#E5B85C] mb-2">
          Mon compte
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
          Mon profil
        </h1>
        <p className="text-sm text-slate-400 mt-2">
          Modifie ton nom, ton poste et ton numéro de maillot.
        </p>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-white/10 p-6">
        <AvatarSection user={user} refreshUser={refreshUser} />

        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
              <UserIcon className="w-3 h-3" /> Nom complet
            </label>
            <input
              data-testid="profile-name-input"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white"
              placeholder="Ton prénom et nom"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                <Hash className="w-3 h-3" /> Numéro de maillot
              </label>
              <input
                data-testid="profile-jersey-input"
                type="number"
                min="0"
                max="99"
                value={form.jersey_number}
                onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white font-mono-stat font-bold text-center"
                placeholder="10"
              />
            </div>
            <div>
              <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Poste
              </label>
              <select
                data-testid="profile-position-select"
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg focus:border-[#E5B85C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/20 text-white"
              >
                <option value="">— Choisir —</option>
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono-stat uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
              <Cake className="w-3 h-3 text-[#E5B85C]" /> Date de naissance (jour et mois)
            </label>
            <MonthDayPicker
              testIdPrefix="profile-birthday"
              value={form.birthday || ""}
              onChange={(v) => setForm({ ...form, birthday: v })}
            />
            <div className="mt-1 text-[11px] text-slate-500">
              Le jour J, tu recevras une notification signée du club 🎂 — pas besoin de l'année.
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              data-testid="profile-save-button"
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-white/10 p-6">
        <NotificationSettings />
      </div>

      <div className="p-5 rounded-xl bg-slate-900 border border-white/10 text-xs text-slate-400">
        Pour changer ton mot de passe, déconnecte-toi puis clique "Mot de passe oublié ?"
        sur l'écran de connexion.
      </div>
    </div>
  );
}

function AvatarSection({ user, refreshUser }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    let localUrl = null;
    async function load() {
      if (!user?.has_avatar) {
        setPreview(null);
        return;
      }
      try {
        const res = await fetch(`${API}/players/${user.id}/avatar?t=${nonce}`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        if (!alive) return;
        localUrl = URL.createObjectURL(blob);
        setPreview(localUrl);
      } catch (e) {
        setPreview(null);
      }
    }
    load();
    return () => {
      alive = false;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [user?.id, user?.has_avatar, nonce]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (max 3 Mo)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/players/${user.id}/avatar`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) throw new Error();
      invalidateAvatar(user.id);
      await refreshUser();
      setNonce((n) => n + 1);
      toast.success("Photo mise à jour");
    } catch (err) {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!confirm("Supprimer ta photo de profil ?")) return;
    try {
      await api.delete(`/players/${user.id}/avatar`);
      invalidateAvatar(user.id);
      await refreshUser();
      setPreview(null);
      toast.success("Photo supprimée");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  return (
    <div className="flex items-center gap-4">
      {preview ? (
        <img
          src={preview}
          alt={user.name}
          data-testid="profile-avatar-preview"
          className="w-20 h-20 rounded-xl object-cover ring-2 ring-[#E5B85C]/40"
        />
      ) : (
        <div
          data-testid="profile-avatar-fallback"
          className="w-20 h-20 rounded-xl bg-slate-800 flex items-center justify-center font-mono-stat font-bold text-2xl text-slate-400"
        >
          {user?.jersey_number || (user?.name || "?").trim().charAt(0).toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-lg uppercase truncate">
          {user.name}
        </div>
        <div className="text-xs text-slate-400 font-mono-stat uppercase tracking-widest truncate">
          {user.email} · {user.role === "admin" ? "Directeur technique" : user.role === "coach" ? "Entraîneur" : "Joueur"}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <label
            data-testid="profile-avatar-upload-button"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide cursor-pointer ${
              uploading ? "bg-slate-700 text-slate-400" : "bg-[#E5B85C] text-[#0B0F17] hover:bg-[#D4A24E]"
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            <input
              data-testid="profile-avatar-file-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={upload}
              disabled={uploading}
            />
            {uploading ? "Upload..." : user.has_avatar ? "Changer" : "Ajouter"}
          </label>
          {user.has_avatar && (
            <button
              type="button"
              data-testid="profile-avatar-delete-button"
              onClick={remove}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-400 hover:text-rose-300 text-xs font-bold uppercase tracking-wide"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationSettings() {
  const [state, setState] = useState("loading"); // loading | supported | subscribed | denied | unsupported | ios-install
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!isPushSupported()) {
      setState("unsupported");
      return;
    }
    if (iosNeedsInstall()) {
      setState("ios-install");
      return;
    }
    if (typeof Notification !== "undefined" && Notification.permission === "denied") {
      setState("denied");
      return;
    }
    const sub = await currentSubscription();
    setState(sub ? "subscribed" : "supported");
  };

  useEffect(() => {
    refresh();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Notifications activées");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Impossible d'activer");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Notifications désactivées");
      await refresh();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      if (r.sent > 0) toast.success("Test envoyé — regarde tes notifs");
      else toast.error("Aucun abonnement actif — active d'abord");
    } catch (e) {
      toast.error("Test échoué");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="notification-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#E5B85C]/20 flex items-center justify-center">
          <Bell className="w-5 h-5 text-[#E5B85C]" />
        </div>
        <div>
          <div className="font-display font-bold uppercase tracking-wide">Notifications push</div>
          <div className="text-xs text-slate-400">
            Nouveaux matchs, rappels J-3 / J-1 et résultats.
          </div>
        </div>
      </div>

      {state === "loading" && (
        <div className="text-xs text-slate-500">Chargement…</div>
      )}

      {state === "unsupported" && (
        <div className="text-sm text-slate-400 flex items-center gap-2">
          <BellOff className="w-4 h-4" />
          Ce navigateur ne supporte pas les notifications push.
        </div>
      )}

      {state === "ios-install" && (
        <div className="rounded-xl bg-slate-800/60 border border-[#E5B85C]/30 p-4 text-sm text-slate-300">
          Sur iPhone, les notifications push web ne fonctionnent qu'après l'installation
          de l'app sur ton écran d'accueil.{" "}
          <a
            data-testid="push-ios-install-link"
            href="/install"
            className="text-[#E5B85C] underline underline-offset-2 font-bold"
          >
            Installer l'app
          </a>
        </div>
      )}

      {state === "denied" && (
        <div className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-4 text-sm text-rose-200">
          Les notifications sont bloquées dans les réglages de ton navigateur.
          Autorise-les depuis l'icône cadenas de la barre d'adresse pour les recevoir.
        </div>
      )}

      {state === "supported" && (
        <button
          data-testid="push-enable-button"
          onClick={enable}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide text-sm disabled:opacity-60"
        >
          <Bell className="w-4 h-4" /> Activer les notifications
        </button>
      )}

      {state === "subscribed" && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-mono-stat uppercase tracking-wide">
            <Bell className="w-3.5 h-3.5" /> Activées
          </span>
          <button
            data-testid="push-test-button"
            onClick={test}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase tracking-wide disabled:opacity-60"
          >
            <Send className="w-3.5 h-3.5" /> Tester
          </button>
          <button
            data-testid="push-disable-button"
            onClick={disable}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-400 hover:text-rose-300 text-xs font-bold uppercase tracking-wide disabled:opacity-60"
          >
            <BellOff className="w-3.5 h-3.5" /> Désactiver
          </button>
        </div>
      )}
    </div>
  );
}

